import { StepPage } from 'containers/StepPage/StepPage';
import React, { useCallback, useMemo } from 'react';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Redirect } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import i18n from 'i18n';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { FlowSummarySection } from 'components/FlowSummarySection/FlowSummarySection';
import { BeaconMessageSetupFlowModel } from './BeaconMessageSetupPageModel';
import { BeaconMessageFormType } from 'core/beaconMessage/BeaconMessage';
import { BeaconMessageForm } from './BeaconMessageForm';
import { Page } from 'components/Page/Page';
import styles from './beaconMessageSetupPage.module.scss';
import { getBeaconMessageSummarySections } from './BeaconMessageSummaryModel';

export const BeaconMessageSetupPage: React.FC<{
  useModel: () => BeaconMessageSetupFlowModel;
}> = ({ useModel }) => {
  const {
    type,
    title,
    loading,
    beaconMessage,
    defaultBeaconMessage,
    redirectPath,
    readonly,
    beaconConnectionOptions,
    availableChannels,
    setBeaconMessage,
    backToList,
    resetRedirectPath,
    handleSubmit
  } = useModel();

  const renderForm = useCallback(
    ({ goNext }) => {
      const onSubmit = (beaconMessage: BeaconMessageFormType) => {
        setBeaconMessage(beaconMessage);
        goNext();
      };

      return (
        <BeaconMessageForm
          type={type}
          formData={beaconMessage!}
          // limitationModel={limitationModel}
          availableChannels={availableChannels}
          defaultBeaconMessage={defaultBeaconMessage!}
          beaconConnectionOptions={beaconConnectionOptions}
          onSubmit={onSubmit}
          // onChannelGroupSettingChange={onChannelGroupSettingChange}
          onNavigationCancel={resetRedirectPath}
        />
      );
    },
    [
      type,
      beaconMessage,
      // limitationModel,
      beaconConnectionOptions,
      defaultBeaconMessage,
      availableChannels,
      setBeaconMessage,
      // onChannelGroupSettingChange,
      resetRedirectPath
    ]
  );

  const renderSummary = useCallback(() => {
    const summarySections = getBeaconMessageSummarySections(beaconMessage);
    if (!summarySections) return <div />;
    const { basicSummaryData, creativeSummaryData } = summarySections;
    return (
      <Page.Section
        title={i18n.t('beaconMessageSetupFlowPage.titles.summaryStep')}
        className={styles.summaryStep}
      >
        <FlowSummarySection {...basicSummaryData} />
        <FlowSummarySection {...creativeSummaryData} />
        {/* {deviceSummaryData && <FlowSummarySection {...deviceSummaryData} />} */}
      </Page.Section>
    );
  }, [beaconMessage]);

  const renderSummaryBtns = useCallback(
    ({ goLast }) => {
      if (!beaconMessage) {
        return <div />;
      }

      if (readonly) {
        return (
          <Button variant='primary' size='sm' onClick={backToList}>
            {i18n.t('common.buttons.ok')}
          </Button>
        );
      }

      const onSubmit = () => {
        handleSubmit(beaconMessage);
      };
      return (
        <>
          <Button size='sm' onClick={onSubmit}>
            {i18n.t('common.buttons.submit')}
          </Button>
          <Button variant='secondary' size='sm' onClick={goLast}>
            {i18n.t('common.buttons.back')}
          </Button>
        </>
      );
    },
    [beaconMessage, readonly, handleSubmit, backToList]
  );

  const breadcrumbRoutes = useMemo(
    () => [
      {
        path: '/beacon-messages',
        breadcrumb: i18n.t(
          'appMenus.beaconMessageManagement.items.beaconMessages'
        )
      },
      {
        path: '/beacon-messages/new',
        breadcrumb: i18n.t('beaconMessageSetupFlowPage.titles.create')
      },
      {
        path: '/beacon-messages/:beaconMessageId',
        breadcrumb: DynamicBreadcrumb,
        props: {
          label: beaconMessage ? beaconMessage.name : '',
          matchParam: 'beaconMessageId'
        }
      }
    ],
    [beaconMessage]
  );

  if (!beaconMessage || !defaultBeaconMessage) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {loading && <LoadingIndicator />}
      {redirectPath && <Redirect to={redirectPath} />}
      <StepPage
        cancel={backToList}
        title={title}
        spaceBetweenTopAreaAndContent={false}
        breadcrumbsRoutes={breadcrumbRoutes}
      >
        {!readonly && (
          <StepPage.Step
            label={i18n.t('beaconMessageSetupFlowPage.titles.formStep')}
            renderStepContent={renderForm}
          />
        )}
        <StepPage.Step
          label={i18n.t('beaconMessageSetupFlowPage.titles.summaryStep')}
          renderStepContent={renderSummary}
          renderBtns={renderSummaryBtns}
        />
      </StepPage>
    </>
  );
};
